import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Check, DollarSign, Download, Loader2, Tag, X } from "lucide-react";
import { usePlayer } from "@/context/PlayerContext";
import { TIERS } from "@/lib/tiers";
import { api, API, fileUrl, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { getFreeUnlockToken } from "@/components/FreeUnlockModal";

export const LicenseModal = () => {
  const { licenseBeat: beat, closeLicense, openFreeDownload } = usePlayer();
  const [loadingTier, setLoadingTier] = useState(null);
  const [paypalOn, setPaypalOn] = useState(false);
  const [promoCode, setPromoCode] = useState("");
  const [promo, setPromo] = useState(null);
  const [promoBusy, setPromoBusy] = useState(false);
  const [offerOpen, setOfferOpen] = useState(false);
  const [offerForm, setOfferForm] = useState({ name: "", email: "", offer_amount: "", notes: "" });
  const [offerBusy, setOfferBusy] = useState(false);
  const [offerSent, setOfferSent] = useState(false);

  useEffect(() => {
    if (beat) {
      setPromo(null);
      setPromoCode("");
      setOfferOpen(false);
      setOfferSent(false);
      setOfferForm({ name: "", email: "", offer_amount: "", notes: "" });
      api.get("/payments/paypal/configured").then((r) => setPaypalOn(r.data.configured)).catch(() => {});
    }
  }, [beat]);

  const submitOffer = async (e) => {
    e.preventDefault();
    setOfferBusy(true);
    try {
      await api.post("/beats/exclusive-offer", {
        beat_id: beat.id,
        offer_amount: parseFloat(offerForm.offer_amount),
        name: offerForm.name,
        email: offerForm.email,
        notes: offerForm.notes,
        origin_url: window.location.origin,
      });
      setOfferSent(true);
      toast.success("Offer sent to PRODRUSHI");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not send offer");
    } finally {
      setOfferBusy(false);
    }
  };

  const applyPromo = async () => {
    if (!promoCode.trim()) return;
    setPromoBusy(true);
    try {
      const { data } = await api.post("/discounts/validate", { code: promoCode.trim() });
      setPromo(data);
      toast.success(`${data.percent}% off applied`);
    } catch {
      setPromo(null);
      toast.error("Invalid or expired code");
    } finally {
      setPromoBusy(false);
    }
  };

  const finalPrice = (tierId) => {
    const p = beat.prices?.[tierId] ?? 0;
    return promo ? p * (1 - promo.percent / 100) : p;
  };

  const buy = async (tierId, provider = "stripe") => {
    setLoadingTier(`${provider}-${tierId}`);
    try {
      const endpoint = provider === "paypal" ? "/payments/paypal/checkout-beat" : "/payments/checkout-beat";
      const { data } = await api.post(endpoint, {
        beat_id: beat.id,
        tier: tierId,
        origin_url: window.location.origin,
        discount_code: promo?.code || "",
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
      setLoadingTier(null);
    }
  };

  return (
    <AnimatePresence>
      {beat && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[60] flex items-end justify-center bg-black/80 p-0 backdrop-blur-sm sm:items-center sm:p-6"
          onClick={closeLicense}
          data-testid="license-modal"
        >
          <motion.div
            initial={{ y: 60, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 60, opacity: 0 }}
            transition={{ type: "spring", damping: 28, stiffness: 260 }}
            onClick={(e) => e.stopPropagation()}
            className="max-h-[90vh] w-full max-w-3xl overflow-y-auto rounded-t-3xl border border-white/10 bg-[#141419] sm:rounded-3xl"
          >
            <div className="flex items-center justify-between border-b border-white/10 p-5 sm:p-6">
              <div className="flex items-center gap-4">
                <img src={fileUrl(beat.cover_url)} alt={beat.title} className="h-14 w-14 rounded-xl object-cover" />
                <div>
                  <p className="font-mono text-[11px] uppercase tracking-widest text-emerald-300">Choose your license</p>
                  <h3 className="font-heading text-xl font-bold text-white" data-testid="license-modal-beat-title">{beat.title}</h3>
                </div>
              </div>
              <button data-testid="license-modal-close" onClick={closeLicense}
                className="rounded-full border border-white/10 p-2 text-zinc-400 transition-colors hover:text-white">
                <X size={18} />
              </button>
            </div>

            <div className="border-b border-white/10 px-5 py-4 sm:px-6" data-testid="promo-code-row">
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Tag size={14} className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-600" />
                  <input
                    value={promoCode}
                    onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                    placeholder="Discount code"
                    data-testid="promo-code-input"
                    className="w-full rounded-full border border-white/10 bg-[#0B0B0E] py-2.5 pl-10 pr-4 font-mono text-sm uppercase tracking-wider text-white placeholder-zinc-600 outline-none focus:border-emerald-300/60"
                  />
                </div>
                <button
                  onClick={applyPromo}
                  disabled={promoBusy}
                  data-testid="promo-apply-button"
                  className="rounded-full border border-emerald-300/40 px-5 text-xs font-bold uppercase tracking-wider text-emerald-200 transition-colors hover:bg-emerald-300/10 disabled:opacity-50"
                >
                  {promoBusy ? <Loader2 size={13} className="animate-spin" /> : "Apply"}
                </button>
              </div>
              {promo && (
                <p className="mt-2 font-mono text-[11px] uppercase tracking-wider text-emerald-400" data-testid="promo-applied-label">
                  {promo.code} — {promo.percent}% off applied
                </p>
              )}
            </div>

            <div className={`grid gap-4 p-5 sm:p-6 ${beat.free_download ? "sm:grid-cols-2 lg:grid-cols-4" : "sm:grid-cols-3"}`}>
              {beat.free_download && (
                <div
                  data-testid="license-tier-free"
                  className="flex flex-col rounded-2xl border border-dashed border-emerald-300/40 bg-emerald-300/5 p-5"
                >
                  <div className="flex items-baseline justify-between">
                    <h4 className="font-heading font-bold text-white">Free Download</h4>
                    <span className="font-heading text-xl font-extrabold text-emerald-200">$0</span>
                  </div>
                  <ul className="mt-3 flex-1 space-y-2">
                    {["Tagged MP3 File", "Non-Profit Use Only (demos, writing)", "Must Credit prod.rushi", "Upgrade Anytime — Buy a Lease to Release"].map((f) => (
                      <li key={f} className="flex items-start gap-2 text-xs text-zinc-400">
                        <Check size={13} className="mt-0.5 shrink-0 text-emerald-300" /> {f}
                      </li>
                    ))}
                  </ul>
                  {getFreeUnlockToken(beat.id) ? (
                    <a
                      href={`${API}/download/free/${getFreeUnlockToken(beat.id)}`}
                      data-testid="license-tier-free-download"
                      className="mt-4 flex items-center justify-center gap-2 rounded-full border border-emerald-300/50 py-2.5 text-xs font-bold uppercase tracking-wider text-emerald-200 transition-colors hover:bg-emerald-300/10"
                    >
                      <Download size={14} /> Download Free MP3
                    </a>
                  ) : (
                    <button
                      data-testid="license-tier-free-unlock"
                      onClick={() => {
                        closeLicense();
                        setTimeout(() => openFreeDownload(beat), 150);
                      }}
                      className="mt-4 flex items-center justify-center gap-2 rounded-full border border-emerald-300/50 py-2.5 text-xs font-bold uppercase tracking-wider text-emerald-200 transition-colors hover:bg-emerald-300/10"
                    >
                      <Download size={14} /> Unlock Free Download
                    </button>
                  )}
                </div>
              )}
              {TIERS.map((tier) => {
                const base = beat.prices?.[tier.id] ?? 0;
                const price = finalPrice(tier.id);
                const isExclusive = tier.id === "exclusive";
                const offerMode = isExclusive && base <= 0;
                const wavNoStems = tier.id === "wav" && !beat.has_stems;
                const tierName = wavNoStems ? "Premium WAV Lease" : tier.name;
                const tierFeatures = wavNoStems
                  ? ["Untagged 24-bit WAV + MP3", "Up to 500,000 Streams", "Non-Exclusive License"]
                  : tier.features;
                return (
                  <div
                    key={tier.id}
                    data-testid={`license-tier-${tier.id}`}
                    className={`flex flex-col rounded-2xl border p-5 transition-colors ${
                      isExclusive ? "border-emerald-300/50 bg-emerald-300/5" : "border-white/10 bg-[#0B0B0E] hover:border-white/25"
                    }`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <h4 className="font-heading font-bold text-white">{tierName}</h4>
                      <span className="flex shrink-0 flex-col items-end leading-tight">
                        {offerMode ? (
                          <span className="font-heading text-sm font-extrabold uppercase tracking-wider text-emerald-200">Make an Offer</span>
                        ) : (
                          <>
                            {promo && <span className="text-[11px] text-zinc-600 line-through">${base.toFixed(2)}</span>}
                            <span className="font-heading text-xl font-extrabold text-emerald-200">${price.toFixed(2)}</span>
                          </>
                        )}
                      </span>
                    </div>
                    <ul className="mt-3 flex-1 space-y-2">
                      {(offerMode
                        ? ["Full Exclusive Ownership", "Beat Removed from Store", "Unlimited Everything", "You Name the Price — PRODRUSHI Reviews It"]
                        : tierFeatures
                      ).map((f) => (
                        <li key={f} className="flex items-start gap-2 text-xs text-zinc-400">
                          <Check size={13} className="mt-0.5 shrink-0 text-emerald-300" /> {f}
                        </li>
                      ))}
                    </ul>

                    {offerMode ? (
                      offerSent ? (
                        <p className="mt-4 rounded-xl border border-emerald-300/40 bg-emerald-300/10 px-3 py-2.5 text-center text-xs font-bold text-emerald-200" data-testid="offer-sent-msg">
                          Offer sent — PRODRUSHI will reply by email.
                        </p>
                      ) : offerOpen ? (
                        <form onSubmit={submitOffer} className="mt-4 space-y-2" data-testid="offer-form">
                          <input required type="number" step="0.01" min="1" placeholder="Your offer (USD)" value={offerForm.offer_amount}
                            onChange={(e) => setOfferForm({ ...offerForm, offer_amount: e.target.value })}
                            className="w-full rounded-lg border border-white/10 bg-[#0B0B0E] px-3 py-2 text-sm text-white placeholder-zinc-600 outline-none focus:border-emerald-300/60"
                            data-testid="offer-amount-input" />
                          <input required placeholder="Your name" value={offerForm.name}
                            onChange={(e) => setOfferForm({ ...offerForm, name: e.target.value })}
                            className="w-full rounded-lg border border-white/10 bg-[#0B0B0E] px-3 py-2 text-sm text-white placeholder-zinc-600 outline-none focus:border-emerald-300/60"
                            data-testid="offer-name-input" />
                          <input required type="email" placeholder="Your email" value={offerForm.email}
                            onChange={(e) => setOfferForm({ ...offerForm, email: e.target.value })}
                            className="w-full rounded-lg border border-white/10 bg-[#0B0B0E] px-3 py-2 text-sm text-white placeholder-zinc-600 outline-none focus:border-emerald-300/60"
                            data-testid="offer-email-input" />
                          <textarea placeholder="Notes (optional)" rows={2} value={offerForm.notes}
                            onChange={(e) => setOfferForm({ ...offerForm, notes: e.target.value })}
                            className="w-full rounded-lg border border-white/10 bg-[#0B0B0E] px-3 py-2 text-sm text-white placeholder-zinc-600 outline-none focus:border-emerald-300/60"
                            data-testid="offer-notes-input" />
                          <button type="submit" disabled={offerBusy}
                            className="flex w-full items-center justify-center gap-2 rounded-full bg-emerald-300 py-2.5 text-xs font-bold uppercase tracking-wider text-black transition-colors hover:bg-emerald-200 disabled:opacity-50"
                            data-testid="offer-submit-button">
                            {offerBusy ? <Loader2 size={14} className="animate-spin" /> : <DollarSign size={14} />} Send Offer
                          </button>
                        </form>
                      ) : (
                        <button onClick={() => setOfferOpen(true)}
                          className="mt-4 flex items-center justify-center gap-2 rounded-full bg-emerald-300 py-2.5 text-xs font-bold uppercase tracking-wider text-black transition-colors hover:bg-emerald-200"
                          data-testid="make-offer-button">
                          <DollarSign size={14} /> Make an Exclusive Offer
                        </button>
                      )
                    ) : (
                      <>
                        <button
                          data-testid={`license-tier-select-${tier.id}`}
                          onClick={() => buy(tier.id, "stripe")}
                          disabled={loadingTier !== null}
                          className={`mt-4 flex items-center justify-center gap-2 rounded-full py-2.5 text-xs font-bold uppercase tracking-wider transition-all disabled:opacity-50 ${
                            isExclusive
                              ? "bg-emerald-300 text-black hover:bg-emerald-200"
                              : "border border-emerald-300/40 text-emerald-200 hover:bg-emerald-300 hover:text-black"
                          }`}
                        >
                          {loadingTier === `stripe-${tier.id}` ? <Loader2 size={14} className="animate-spin" /> : "Buy with Card"}
                        </button>
                        {paypalOn && (
                          <button
                            data-testid={`license-tier-paypal-${tier.id}`}
                            onClick={() => buy(tier.id, "paypal")}
                            disabled={loadingTier !== null}
                            className="mt-2 flex items-center justify-center gap-2 rounded-full border border-white/15 py-2.5 text-xs font-bold uppercase tracking-wider text-zinc-300 transition-all hover:border-white/40 disabled:opacity-50"
                          >
                            {loadingTier === `paypal-${tier.id}` ? <Loader2 size={14} className="animate-spin" /> : "Pay with PayPal"}
                          </button>
                        )}
                      </>
                    )}
                  </div>
                );
              })}
            </div>
            <p className="border-t border-white/10 px-6 py-4 text-center text-xs text-zinc-600">
              Secure checkout via Stripe{paypalOn ? " or PayPal" : ""} · Instant download + PDF license after payment
            </p>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
